import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import api from '../../services/api';
import {
  fetchFolders, fetchDocuments, fetchBreadcrumbs, fetchFolderTree,
  createFolder, renameFolder, deleteFolder,
  uploadDocument, deleteDocument, updateDocument,
  setCurrentFolder, setSearch,
  linkDocumentToProject, linkFolderToProject,
  unlinkDocumentFromProject, unlinkFolderFromProject,
} from '../../store/slices/librarySlice';
import { fetchProjects } from '../../store/slices/projectsSlice';
import { useUploadManager } from '../../contexts/UploadManagerContext';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import ProjectBadges from '../../components/common/ProjectBadges';
import FileExplorer from '../../components/library/FileExplorer';
import './LibraryPage.css';

const LibraryPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { documents, folders, currentFolderId, breadcrumbs, pagination, loading, uploading, search, folderTree } = useSelector((state) => state.library);
  const { projects } = useSelector((state) => state.projects);
  const { addUpload, downloadFile } = useUploadManager();

  const [viewMode, setViewMode] = useState('list');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameName, setRenameName] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);
  const searchTimerRef = useRef(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, title: '', message: '', confirmText: 'Delete',
    variant: 'danger', onConfirm: null, isLoading: false, details: null,
  });

  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const canManage = ['super_admin', 'admin', 'manager'].includes(user?.role);

  // Handle URL parameters on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const folderParam = searchParams.get('folder');
    if (folderParam) {
      const folderId = parseInt(folderParam, 10);
      if (!isNaN(folderId)) {
        dispatch(setCurrentFolder(folderId));
      }
    }
  }, [dispatch, location.search]);

  // Load data
  const loadData = useCallback(() => {
    dispatch(fetchFolders({ parentId: currentFolderId, search }));
    dispatch(fetchDocuments({
      folderId: currentFolderId,
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      allFolders: !!search,
    }));
    if (currentFolderId) {
      dispatch(fetchBreadcrumbs(currentFolderId));
    }
  }, [dispatch, currentFolderId, search, pagination.page, pagination.pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    dispatch(fetchProjects({ page: 1, pageSize: 100 }));
    dispatch(fetchFolderTree());
  }, [dispatch]);

  // Close context menu on outside click, scroll, or resize
  useEffect(() => {
    const onMouseDown = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    const onClose = () => setContextMenu(null);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, []);

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSearch(value));
    }, 300);
  };

  // Navigation
  const navigateToFolder = (folderId) => {
    dispatch(setCurrentFolder(folderId));
    dispatch(setSearch(''));
    setSearchInput('');
    // Update URL to enable deep linking
    if (folderId) {
      navigate(`/library?folder=${folderId}`, { replace: true });
    } else {
      navigate('/library', { replace: true });
    }
  };

  const navigateToRoot = () => {
    navigateToFolder(null);
  };

  // Upload
  const onDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      // Use upload manager for background uploads with progress tracking
      addUpload(file, {
        folderId: currentFolderId,
        onSuccess: (document) => {
          toast.success(`"${file.name}" uploaded successfully`);
          loadData(); // Refresh the list
        },
        onError: (error) => {
          toast.error(error?.response?.data?.detail || error.message || `Failed to upload "${file.name}"`);
        }
      });
    }
    setShowUploadModal(false);
  }, [addUpload, currentFolderId, loadData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true, // Disable click for getRootProps (we'll trigger manually)
    noKeyboard: true,
  });

  // Ref for file input (direct access for Upload button)
  const fileInputRef = useRef(null);

  // Handle direct upload button click
  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await dispatch(createFolder({ name: newFolderName.trim(), parentId: currentFolderId })).unwrap();
      toast.success('Folder created');
      setShowCreateFolderModal(false);
      setNewFolderName('');
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to create folder');
    }
  };

  // Rename
  const handleRename = async () => {
    if (!renameName.trim() || !renameTarget) return;
    try {
      if (renameTarget.type === 'folder') {
        await dispatch(renameFolder({ folderId: renameTarget.id, name: renameName.trim() })).unwrap();
      } else {
        await dispatch(updateDocument({ documentId: renameTarget.id, data: { original_filename: renameName.trim() } })).unwrap();
      }
      toast.success('Renamed successfully');
      setShowRenameModal(false);
      setRenameTarget(null);
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to rename');
    }
  };

  // Delete
  const getLinkedProjectsDetails = (item) => {
    const linked = item?.linked_projects || [];
    if (linked.length === 0) return null;
    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '12px', color: '#8b8f96', marginBottom: '6px' }}>Linked projects</div>
        <ProjectBadges projects={linked} maxVisible={4} size="sm" />
      </div>
    );
  };

  const handleDeleteFolder = (folder) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Folder',
      message: `Are you sure you want to delete "${folder.name}" and all its contents? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      isLoading: false,
      details: getLinkedProjectsDetails(folder),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteFolder(folder.id)).unwrap();
          toast.success('Folder deleted');
          loadData();
        } catch (err) {
          toast.error(err || 'Failed to delete folder');
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      },
    });
  };

  const handleDeleteDocument = (doc) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Document',
      message: `Are you sure you want to delete "${doc.original_filename}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      isLoading: false,
      details: getLinkedProjectsDetails(doc),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteDocument(doc.id)).unwrap();
          toast.success('Document deleted');
          loadData();
        } catch (err) {
          toast.error(err || 'Failed to delete document');
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      },
    });
  };

  const getLinkedProjectIds = (item) => {
    const linked = item?.linked_projects || [];
    return linked.map((p) => p.id.toString());
  };

  // Link to project or unlink
  const handleLinkToProject = async () => {
    if (!linkTarget) return;
    try {
      const initialLinkedIds = getLinkedProjectIds(linkTarget).map(id => parseInt(id));
      const newLinkedIds = selectedProjectIds.map(id => parseInt(id));
      
      // Find projects to link and unlink
      const toLink = newLinkedIds.filter(id => !initialLinkedIds.includes(id));
      const toUnlink = initialLinkedIds.filter(id => !newLinkedIds.includes(id));

      // Link new projects
      if (toLink.length > 0) {
        await api.post('/library/links/multiple-projects', {
          document_id: linkTarget.type === 'document' ? linkTarget.id : null,
          folder_id: linkTarget.type === 'folder' ? linkTarget.id : null,
          project_ids: toLink
        });
      }

      // Unlink removed projects
      if (toUnlink.length > 0) {
        await Promise.all(
          toUnlink.map((projectId) => {
            if (linkTarget.type === 'folder') {
              return dispatch(unlinkFolderFromProject({ folderId: linkTarget.id, projectId })).unwrap();
            }
            return dispatch(unlinkDocumentFromProject({ documentId: linkTarget.id, projectId })).unwrap();
          })
        );
      }

      const message = [];
      if (toLink.length > 0) message.push(`linked to ${toLink.length} project(s)`);
      if (toUnlink.length > 0) message.push(`unlinked from ${toUnlink.length} project(s)`);
      const successMsg = message.length > 0 ? message.join(' and ') + ' successfully' : 'No changes';
      if (message.length > 0) toast.success(successMsg);
      setShowLinkModal(false);
      setLinkTarget(null);
      setSelectedProjectIds([]);
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to update project links');
    }
  };

  // Move item (drag-drop)
  const handleMove = async (itemType, itemId, targetFolderId) => {
    try {
      const response = await api.post('/library/move', {
        item_type: itemType,
        item_id: itemId,
        target_folder_id: targetFolderId,
      });

      toast.success(`${itemType === 'folder' ? 'Folder' : 'Document'} moved successfully`);
      loadData(); // Reload current view
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to move item');
      throw err;
    }
  };

  // Move document
  const handleMoveDocument = async (targetFolderId) => {
    if (!moveTarget) return;
    try {
      await dispatch(updateDocument({
        documentId: moveTarget.id,
        data: { folder_id: targetFolderId === null ? -1 : targetFolderId }
      })).unwrap();
      toast.success('Document moved');
      setShowMoveModal(false);
      setMoveTarget(null);
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to move document');
    }
  };

  // Context menu
  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    e.stopPropagation();
    const menuWidth = 200;
    const menuHeight = 260;
    const x = Math.min(e.clientX, window.innerWidth - menuWidth);
    const y = Math.min(e.clientY, window.innerHeight - menuHeight);
    setContextMenu({
      x,
      y,
      item: { ...item, type },
    });
  };

  // Handle three-dot action menu
  const handleFolderAction = (action, folder, btnEl) => {
    if (action === 'menu') {
      const rect = (btnEl || document.activeElement).getBoundingClientRect();
      const menuWidth = 200;
      // Folder menu: Rename + Link to Project + divider + Delete = 4 items ≈ 160px
      const menuHeight = 160;
      const x = Math.max(4, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 4));
      const spaceBelow = window.innerHeight - rect.bottom;
      const y = spaceBelow >= menuHeight + 4
        ? rect.bottom + 4
        : Math.max(4, rect.top - menuHeight - 4);
      setContextMenu({ x, y, item: { ...folder, type: 'folder' } });
    }
  };

  const handleFileAction = (action, doc, btnEl) => {
    if (action === 'menu') {
      const rect = (btnEl || document.activeElement).getBoundingClientRect();
      const menuWidth = 200;
      // Document menu: Open + Download + Rename + Move to + Link to Project + divider + Delete = 6 items ≈ 225px
      const menuHeight = 225;
      const x = Math.max(4, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 4));
      const spaceBelow = window.innerHeight - rect.bottom;
      const y = spaceBelow >= menuHeight + 4
        ? rect.bottom + 4
        : Math.max(4, rect.top - menuHeight - 4);
      setContextMenu({ x, y, item: { ...doc, type: 'document' } });
    }
  };

  const handleOpenLinkModal = (item) => {
    setLinkTarget(item);
    setSelectedProjectIds(getLinkedProjectIds(item));
    setShowLinkModal(true);
  };

  // Handle file click - open the file
  const handleFileClick = (doc) => {
    const url = doc.viewer_url || doc.direct_url;
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Get file icon
  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return '🖼';
    if (contentType?.startsWith('video/')) return '🎬';
    if (contentType?.startsWith('audio/')) return '🎵';
    if (contentType === 'application/pdf') return '📄';
    if (contentType?.includes('word') || contentType?.includes('document')) return '📝';
    if (contentType?.includes('sheet') || contentType?.includes('excel')) return '📊';
    if (contentType?.includes('presentation') || contentType?.includes('powerpoint')) return '📑';
    if (contentType?.includes('zip') || contentType?.includes('rar') || contentType?.includes('7z')) return '📦';
    return '📎';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Pagination
  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div className="library-page">
      {/* Header */}
      <div className="library-header">
        <div className="library-header-left">
          <h1 className="library-title">Library</h1>
          <span className="library-count">{pagination.total} document{pagination.total !== 1 ? 's' : ''}</span>
        </div>
        <div className="library-header-right">
          <div className="library-search">
            <svg className="library-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M11.742 10.344a6.5 6.5 0 10-1.397 1.398h-.001l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85-.017.016zm-5.442.656a5 5 0 110-10 5 5 0 010 10z" clipRule="evenodd" />
            </svg>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchInput}
              onChange={handleSearchChange}
              className="library-search-input"
            />
          </div>
          <div className="library-view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 2.5A1.5 1.5 0 012.5 1h3A1.5 1.5 0 017 2.5v3A1.5 1.5 0 015.5 7h-3A1.5 1.5 0 011 5.5v-3zm8 0A1.5 1.5 0 0110.5 1h3A1.5 1.5 0 0115 2.5v3A1.5 1.5 0 0113.5 7h-3A1.5 1.5 0 019 5.5v-3zm-8 8A1.5 1.5 0 012.5 9h3A1.5 1.5 0 017 10.5v3A1.5 1.5 0 015.5 15h-3A1.5 1.5 0 011 13.5v-3zm8 0A1.5 1.5 0 0110.5 9h3a1.5 1.5 0 011.5 1.5v3a1.5 1.5 0 01-1.5 1.5h-3A1.5 1.5 0 019 13.5v-3z" />
              </svg>
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M2.5 12a.5.5 0 01.5-.5h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5zm0-4a.5.5 0 01.5-.5h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5zm0-4a.5.5 0 01.5-.5h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {canManage && (
            <div className="library-header-actions">
              <button className="btn btn-primary btn-sm" onClick={() => { setNewFolderName(''); setShowCreateFolderModal(true); }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M.5 3l.04.87a1.99 1.99 0 00-.342 1.311l.637 7A2 2 0 002.826 14h10.348a2 2 0 001.991-1.819l.637-7A2 2 0 0013.81 3H9.828a2 2 0 01-1.414-.586l-.828-.828A2 2 0 006.172 1H2.5a2 2 0 00-2 2z" />
                </svg>
                New Folder
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleUploadButtonClick}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z" />
                  <path d="M7.646 1.146a.5.5 0 01.708 0l3 3a.5.5 0 01-.708.708L8.5 2.707V11.5a.5.5 0 01-1 0V2.707L5.354 4.854a.5.5 0 11-.708-.708l3-3z" />
                </svg>
                Upload
              </button>
              {/* Hidden file input for direct upload */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    onDrop(files);
                  }
                  e.target.value = ''; // Reset input
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="library-breadcrumbs">
        <button className="breadcrumb-item" onClick={navigateToRoot}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8.354 1.146a.5.5 0 00-.708 0l-6 6A.5.5 0 001.5 7.5v7a.5.5 0 00.5.5h4.5a.5.5 0 00.5-.5v-4h2v4a.5.5 0 00.5.5H14a.5.5 0 00.5-.5v-7a.5.5 0 00-.146-.354L8.354 1.146z" />
          </svg>
          Library
        </button>
        {breadcrumbs.map((bc, idx) => (
          <React.Fragment key={bc.id}>
            <span className="breadcrumb-sep">/</span>
            <button
              className={`breadcrumb-item ${idx === breadcrumbs.length - 1 ? 'current' : ''}`}
              onClick={() => idx < breadcrumbs.length - 1 ? navigateToFolder(bc.id) : null}
              disabled={idx === breadcrumbs.length - 1}
            >
              {bc.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      <div className="library-content">
        <FileExplorer
          folders={folders}
          documents={documents}
          viewMode={viewMode}
          canManage={canManage}
          onFolderClick={(folder) => navigateToFolder(folder.id)}
          onFileClick={handleFileClick}
          onFolderContextMenu={(e, folder) => handleContextMenu(e, folder, 'folder')}
          onFileContextMenu={(e, doc) => handleContextMenu(e, doc, 'document')}
          onFolderAction={handleFolderAction}
          onFileAction={handleFileAction}
          onMove={handleMove}
          getFileIcon={getFileIcon}
          formatDate={formatDate}
          loading={loading}
          emptyMessage={
            search
              ? 'No results found. Try a different search term.'
              : 'This folder is empty. Upload files or create folders to get started.'
          }
        />

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="library-pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={pagination.page <= 1}
              onClick={() => dispatch(fetchDocuments({
                folderId: currentFolderId, search, page: pagination.page - 1,
                pageSize: pagination.pageSize, allFolders: !!search,
              }))}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={pagination.page >= totalPages}
              onClick={() => dispatch(fetchDocuments({
                folderId: currentFolderId, search, page: pagination.page + 1,
                pageSize: pagination.pageSize, allFolders: !!search,
              }))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="library-context-menu"
          ref={contextMenuRef}
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {contextMenu.item.type === 'document' && (
            <>
              <button className="context-item" onClick={() => {
                const url = contextMenu.item.viewer_url || contextMenu.item.direct_url;
                if (url) window.open(url, '_blank');
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 011.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0114.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 011.172 8z" />
                  <path d="M8 5.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" />
                </svg>
                Open
              </button>
              <button className="context-item" onClick={() => {
                downloadFile(contextMenu.item);
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z" />
                  <path d="M7.646 11.854a.5.5 0 00.708 0l3-3a.5.5 0 00-.708-.708L8.5 10.293V1.5a.5.5 0 00-1 0v8.793L5.354 8.146a.5.5 0 10-.708.708l3 3z" />
                </svg>
                Download
              </button>
            </>
          )}
          {canManage && (
            <>
              <button className="context-item" onClick={() => {
                setRenameTarget(contextMenu.item);
                setRenameName(contextMenu.item.type === 'folder' ? contextMenu.item.name : contextMenu.item.original_filename);
                setShowRenameModal(true);
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M12.146.146a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-10 10a.5.5 0 01-.168.11l-5 2a.5.5 0 01-.65-.65l2-5a.5.5 0 01.11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 01.5.5v.5h.5a.5.5 0 01.5.5v.5h.293l6.5-6.5z" />
                </svg>
                Rename
              </button>
              {contextMenu.item.type === 'document' && (
                <button className="context-item" onClick={() => {
                  setMoveTarget(contextMenu.item);
                  setShowMoveModal(true);
                  setContextMenu(null);
                }}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M1 3.5A1.5 1.5 0 012.5 2h3.879a1.5 1.5 0 011.06.44l1.122 1.12A1.5 1.5 0 009.62 4H13.5A1.5 1.5 0 0115 5.5v7a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 011 12.5v-9z" clipRule="evenodd" />
                  </svg>
                  Move to...
                </button>
              )}
              <button className="context-item" onClick={() => {
                handleOpenLinkModal(contextMenu.item);
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4.715 6.542L3.343 7.914a3 3 0 104.243 4.243l1.828-1.829A3 3 0 008.586 5.5L8 6.086a1.002 1.002 0 00-.154.199 2 2 0 01.861 3.337L6.88 11.45a2 2 0 11-2.83-2.83l.793-.792a4.018 4.018 0 01-.128-1.287z" />
                  <path d="M6.586 4.672A3 3 0 007.414 9.5l.775-.776a2 2 0 01-.896-3.346L9.12 3.55a2 2 0 112.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 10-4.243-4.243L6.586 4.672z" />
                </svg>
                Link to Project
              </button>
              <div className="context-divider" />
              <button className="context-item danger" onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (contextMenu.item.type === 'folder') {
                  handleDeleteFolder(contextMenu.item);
                } else {
                  handleDeleteDocument(contextMenu.item);
                }
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z" />
                  <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H5.707L6.854.146A.5.5 0 017.207 0h1.586a.5.5 0 01.353.146L10.293 1H12.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" clipRule="evenodd" />
                </svg>
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Upload Modal */}
      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title="Upload Files" size="small">
        <div {...getRootProps()} className={`library-dropzone ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6v28M6 20h28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <p className="dropzone-text">
            {isDragActive ? 'Drop files here...' : 'Drag & drop files here, or click to browse'}
          </p>
          <p className="dropzone-hint">All file types supported</p>
        </div>
        {uploading && (
          <div className="upload-progress">
            <span className="spinner"></span>
            <span>Uploading...</span>
          </div>
        )}
      </Modal>

      {/* Create Folder Modal */}
      <Modal isOpen={showCreateFolderModal} onClose={() => setShowCreateFolderModal(false)} title="Create Folder" size="small">
        <div className="form-group">
          <label className="form-label">Folder Name</label>
          <input
            type="text"
            className="form-input"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Enter folder name"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowCreateFolderModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Create</button>
        </div>
      </Modal>

      {/* Rename Modal */}
      <Modal isOpen={showRenameModal} onClose={() => setShowRenameModal(false)} title="Rename" size="small">
        <div className="form-group">
          <label className="form-label">New Name</label>
          <input
            type="text"
            className="form-input"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowRenameModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleRename} disabled={!renameName.trim()}>Save</button>
        </div>
      </Modal>

      {/* Link to Projects Modal (unified) */}
      <Modal isOpen={showLinkModal} onClose={() => { setShowLinkModal(false); setLinkTarget(null); setSelectedProjectIds([]); setLinkSearch(''); }} title="Link to Projects" size="small">
        <div className="form-group">
          <label className="form-label">Select Projects (check to link, uncheck to unlink)</label>
          <div className="search-box" style={{ marginBottom: 'var(--space-2)' }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M9.965 11.026a5 5 0 111.06-1.06l2.755 2.754a.75.75 0 11-1.06 1.06l-2.755-2.754zM10.5 7a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0z" clipRule="evenodd"/>
            </svg>
            <input
              type="text"
              placeholder="Search projects..."
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '4px' }}>
            {projects.filter(p =>
              p.name.toLowerCase().includes(linkSearch.toLowerCase()) ||
              p.key.toLowerCase().includes(linkSearch.toLowerCase())
            ).map((p) => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }} className="link-project-row">
                <input
                  type="checkbox"
                  checked={selectedProjectIds.includes(p.id.toString())}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProjectIds([...selectedProjectIds, p.id.toString()]);
                    } else {
                      setSelectedProjectIds(selectedProjectIds.filter(id => id !== p.id.toString()));
                    }
                  }}
                  style={{ flexShrink: 0, width: '14px', height: '14px', cursor: 'pointer', accentColor: 'var(--color-brand)' }}
                />
                <span style={{ fontWeight: 'var(--weight-medium)', color: 'var(--color-text-primary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', flexShrink: 0, fontFamily: 'var(--font-mono)' }}>{p.key}</span>
              </label>
            ))}
            {projects.filter(p =>
              p.name.toLowerCase().includes(linkSearch.toLowerCase()) ||
              p.key.toLowerCase().includes(linkSearch.toLowerCase())
            ).length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
                No projects match "{linkSearch}"
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => { setShowLinkModal(false); setLinkTarget(null); setSelectedProjectIds([]); setLinkSearch(''); }}>Cancel</button>
          <button className="btn btn-primary" onClick={handleLinkToProject}>Update Project Links</button>
        </div>
      </Modal>

      {/* Move Document Modal */}
      <Modal isOpen={showMoveModal} onClose={() => { setShowMoveModal(false); setMoveTarget(null); }} title="Move to Folder" size="small">
        <div className="move-folder-list">
          <button
            className="move-folder-item"
            onClick={() => handleMoveDocument(null)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8.354 1.146a.5.5 0 00-.708 0l-6 6A.5.5 0 001.5 7.5v7a.5.5 0 00.5.5h4.5a.5.5 0 00.5-.5v-4h2v4a.5.5 0 00.5.5H14a.5.5 0 00.5-.5v-7a.5.5 0 00-.146-.354L8.354 1.146z" />
            </svg>
            Root (No folder)
          </button>
          {folders.filter(f => f.id !== moveTarget?.folder_id).map((f) => (
            <button
              key={f.id}
              className="move-folder-item"
              onClick={() => handleMoveDocument(f.id)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M.54 3.87L.5 3a2 2 0 012-2h3.672a2 2 0 011.414.586l.828.828A2 2 0 009.828 3h3.982a2 2 0 011.992 2.181l-.637 7A2 2 0 0113.174 14H2.826a2 2 0 01-1.991-1.819l-.637-7a1.99 1.99 0 01.342-1.31z" />
              </svg>
              {f.name}
            </button>
          ))}
        </div>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        details={confirmModal.details}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default LibraryPage;
